import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, FolderKanban } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

interface TeamUserRow {
  id: string;
  email: string;
  name: string;
  role: "admin" | "finanzas" | "lead" | "member";
  position: string | null;
  projectCount: number;
}

export const Route = createFileRoute("/team/")({
  component: TeamIndexPage,
});

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

function TeamIndexPage() {
  const { data, isLoading } = useQuery<{ users: TeamUserRow[] }>({
    queryKey: ["team"],
    queryFn: () => api.get("/team"),
  });
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.users;
    return data.users.filter(
      (u) => u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle),
    );
  }, [data?.users, q]);

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="equipo"
        title="Compañeros"
        subtitle="El equipo de Axorial. Hacé click para ver en qué andan."
      />

      <div className="flex items-center gap-2">
        <label className="input input-bordered flex items-center gap-2 max-w-md w-full">
          <Search className="size-4 text-base-content/40" />
          <input
            type="text"
            placeholder="Buscar por nombre o email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="grow"
          />
        </label>
        <span className="text-xs text-base-content/55 ml-auto">
          {filtered.length} de {data.users.length}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map((u) => (
          <Link
            key={u.id}
            to="/team/$userId"
            params={{ userId: u.id }}
            className="card-elevated p-4 hover:border-primary/40 transition-colors group"
          >
            <div className="flex items-start gap-3">
              <div className="size-11 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-semibold text-sm shrink-0">
                {initials(u.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-base-content truncate group-hover:text-primary transition-colors">
                  {u.name}
                </div>
                {/* Puesto > rol como información primaria. El rol (admin/lead)
                    es contexto operativo y solo se muestra si no es member. */}
                <div className="text-xs text-base-content/65 truncate font-medium">
                  {u.position ?? <span className="italic opacity-60">Sin puesto</span>}
                </div>
                <div className="text-[11px] text-base-content/45 truncate">{u.email}</div>
                {u.projectCount > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    <span className="badge badge-ghost badge-xs gap-1">
                      <FolderKanban className="size-2.5" />
                      {u.projectCount}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-base-content/55 py-10">
            Sin resultados para "{q}".
          </div>
        )}
      </div>
    </div>
  );
}
